import React, { useState, useEffect, useCallback } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  InputNumber,
  Divider,
  AutoComplete,
  FloatButton,
  message,
} from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useFacturaForm } from "./hooks/useFacturaForm";
import { ProductTable } from "./components/ProductTable";
import { ProductSidebar } from "./components/ProductSidebar";
import { FacturaResumenModal } from "./components/FacturaResumenModal";
import { ProductService } from "./types/productTypes";
import { formatCurrency, parseCurrency } from "./utils/facturaUtils";
import { calcularIVADesdeBase } from "../../utils/ivaCalculator";
import { useAppSelector } from "../../hooks/reduxHooks";
import {
  getDepartamentos,
  getMunicipios,
} from "../../api/Catalogos/catalogos.api";

const { TextArea } = Input;

const FacturaElectronicaForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Extraer parámetros de URL del pago
  const amortizationId = searchParams.get("amortizationId");
  const transactionId = searchParams.get("transactionId");
  const monto = searchParams.get("monto");
  const provider = searchParams.get("provider");

  // Obtener datos del paciente autenticado
  const authState = useAppSelector((state) => state.auth);

  const {
    form,
    loading,
    cuerpoDocumento,
    catalogos,
    selectedDepartamento,
    addItem,
    removeItem,
    updateItem,
    handleSubmit,
    handleConfirmFactura,
    handleDepartamentoChange,
    getFilteredMunicipios,
    setCuerpoDocumento,
    showResumenModal,
    setShowResumenModal,
    resumenData,
    prefilledData,
    setPrefilledData,
  } = useFacturaForm();

  // Prellenar datos del pago cuando estén disponibles
  useEffect(() => {
    if (amortizationId && transactionId && monto && provider) {
      const paymentData = {
        amortizationId: parseInt(amortizationId),
        transactionId,
        monto: parseFloat(monto),
        provider,
      };
      setPrefilledData(paymentData);

      // Función auxiliar para generar un DUI válido basado en el código del paciente
      const generateDUIFromCode = (codigoPaciente: string): string => {
        // Si ya tiene formato de DUI (########-#), devolverlo
        if (codigoPaciente && /^\d{8}-\d$/.test(codigoPaciente)) {
          return codigoPaciente;
        }

        // Si el código tiene al menos 8 dígitos, usarlos
        const numbers = codigoPaciente.replace(/\D/g, "");
        if (numbers.length >= 8) {
          const base = numbers.slice(0, 8);
          const checkDigit = calculateDUICheckDigit(base);
          return `${base}-${checkDigit}`;
        }

        // Generar DUI genérico basado en ID del paciente
        const pacienteId =
          authState.pacienteData?.pacienteId?.toString() || "1";
        const base = pacienteId.padStart(8, "0").slice(0, 8);
        const checkDigit = calculateDUICheckDigit(base);
        return `${base}-${checkDigit}`;
      };

      // Función para calcular el dígito verificador del DUI
      const calculateDUICheckDigit = (duiBase: string): string => {
        const weights = [9, 8, 7, 6, 5, 4, 3, 2];
        let sum = 0;

        for (let i = 0; i < 8; i++) {
          sum += parseInt(duiBase[i]) * weights[i];
        }

        const remainder = sum % 11;
        const checkDigit = 11 - remainder;

        if (checkDigit === 10) return "0";
        if (checkDigit === 11) return "1";
        return checkDigit.toString();
      };

      // Función auxiliar para resolver códigos de departamento y municipio desde IDs del paciente
      const resolveLocationFromPaciente = async (
        pacienteData: typeof authState.pacienteData
      ) => {
        try {
          // Cargar catálogos para resolver IDs
          const [departamentosData, municipiosData] = await Promise.all([
            getDepartamentos(),
            getMunicipios(),
          ]);

          let codigoDepartamento = "06"; // San Salvador por defecto
          let codigoMunicipio = "14"; // San Salvador por defecto

          // Resolver departamento desde ID
          if (pacienteData?.departamento) {
            const deptId = Number(pacienteData.departamento);
            if (!isNaN(deptId)) {
              const dept = departamentosData.find((d) => d.id === deptId);
              if (dept) {
                codigoDepartamento = dept.codigo;
              }
            } else {
              // Si no es numérico, asumir que ya es un código
              codigoDepartamento = String(pacienteData.departamento);
            }
          }

          // Resolver municipio desde ID
          if (pacienteData?.municipio) {
            const munId = Number(pacienteData.municipio);
            if (!isNaN(munId)) {
              const mun = municipiosData.find((m) => m.id === munId);
              if (mun) {
                codigoMunicipio = mun.codigo;
              }
            } else {
              // Si no es numérico, asumir que ya es un código
              codigoMunicipio = String(pacienteData.municipio);
            }
          }

          return { codigoDepartamento, codigoMunicipio };
        } catch {
          return { codigoDepartamento: "06", codigoMunicipio: "14" }; // Fallback
        }
      };

      // Obtener datos reales del paciente autenticado o usar genéricos como fallback
      const pacienteData = authState.pacienteData;
      const nombreCompleto = pacienteData
        ? `${pacienteData.nombre1} ${pacienteData.apellido1}`.trim()
        : "Cliente Genérico";
      const telefonoPaciente = pacienteData?.telefono || "0000-0000";
      const emailPaciente = pacienteData?.email || "cliente@email.com";
      const direccionPaciente =
        pacienteData?.direccion || "San Salvador, El Salvador";

      // Priorizar DUI real del paciente, luego generar si es necesario
      const duiPaciente =
        pacienteData?.dui &&
        pacienteData.dui !== "" &&
        pacienteData.dui !== null
          ? pacienteData.dui
          : pacienteData?.codigoPaciente
          ? generateDUIFromCode(pacienteData.codigoPaciente)
          : "00000000-0";

      // Resolver códigos de departamento y municipio de forma asíncrona
      resolveLocationFromPaciente(pacienteData).then(
        ({ codigoDepartamento, codigoMunicipio }) => {
          // Actualizar los campos del formulario con valores reales del paciente
          form.setFieldsValue({
            // Datos del emisor (desde .env)
            nit: import.meta.env.VITE_SONRIE_NIT || "06141803690096",

            // Datos del receptor con información real del paciente
            receptor_nombre: nombreCompleto,
            receptor_tipoDocumento: "13",
            receptor_codActividad: "86909",
            receptor_descActividad:
              "Otras actividades de atención de la salud humana",
            receptor_numDocumento: duiPaciente,
            receptor_telefono: telefonoPaciente,
            receptor_correo: emailPaciente,
            receptor_departamento: codigoDepartamento,
            receptor_municipio: codigoMunicipio,
            receptor_complemento: direccionPaciente,
            resumen_numPagoElectronico: transactionId,
            extension_observaciones: `Pago procesado automáticamente. Provider: ${provider.toUpperCase()}, Amount: $${monto}`,
            extension_nombEntrega: "Clínica Sonríe",
            extension_docuEntrega: "06141803690096",
            extension_nombRecibe: nombreCompleto,
            extension_docuRecibe: duiPaciente,
          });
        }
      );

      // Crear un producto automático basado en el pago
      // CORRECCIÓN CRÍTICA: El monto de la amortización es la BASE IMPONIBLE
      const baseImponible = parseFloat(monto);
      const calculoIVA = calcularIVADesdeBase(baseImponible);

      const newItem = {
        key: Date.now(),
        cantidad: 1,
        codigo: `SRV-${amortizationId}`,
        uniMedida: 59,
        descripcion: `Servicio dental - Amortización #${amortizationId}`,
        precioUni: calculoIVA.baseImponible,
        montoDescu: 0,
        ventaGravada: calculoIVA.totalConIVA,
        noGravado: 0,
        tipoItem: 1,
        ventaNoSuj: 0,
        ventaExenta: 0,
        ivaItem: calculoIVA.iva,
        numeroDocumento: null,
        codTributo: "20",
        tributos: null,
        psv: calculoIVA.totalConIVA,
      };

      setCuerpoDocumento([newItem]);

      message.success(
        `Datos del pago cargados automáticamente. Total: $${calculoIVA.totalConIVA} (Base: $${calculoIVA.baseImponible} + IVA: $${calculoIVA.iva})`
      );
    }
  }, [
    amortizationId,
    transactionId,
    monto,
    provider,
    form,
    setCuerpoDocumento,
    setPrefilledData,
    authState,
  ]);

  // Handle product selection from sidebar
  const handleSelectProduct = (product: ProductService) => {
    const calculoIVA = calcularIVADesdeBase(product.precioUnitario);

    const newItem = {
      key: Date.now(),
      cantidad: 1,
      codigo: product.codigo,
      uniMedida: product.unidadMedida,
      descripcion: product.descripcion,
      precioUni: product.precioUnitario,
      montoDescu: 0,
      ventaGravada: calculoIVA.totalConIVA,
      noGravado: 0,
      tipoItem: 1,
      ventaNoSuj: 0,
      ventaExenta: 0,
      ivaItem: calculoIVA.iva,
      numeroDocumento: "",
      codTributo: "20",
      tributos: [],
      psv: calculoIVA.totalConIVA,
    };

    // Add the new item directly to cuerpoDocumento
    setCuerpoDocumento((prevItems) => [...prevItems, newItem]);

    // Close sidebar and show success message
    setSidebarOpen(false);
    message.success(`Producto "${product.descripcion}" agregado a la factura`);
  };

  // Función mejorada para manejar el cambio de departamento
  const handleDepartamentoChangeImproved = useCallback(
    (departamentoCode: string) => {
      // Buscar el departamento por código
      const departamento = catalogos.departamentos.find(
        (dept) => dept.value === departamentoCode
      );

      if (departamento) {
        // Verificar municipios disponibles para este departamento
        const municipiosDisponibles = catalogos.municipios.filter(
          (mun) => mun.departamento_id === departamento.id
        );

        // Usar la función original del hook
        handleDepartamentoChange(departamentoCode);

        // Mostrar información útil para el usuario
        message.info(
          `Departamento seleccionado: ${departamento.label}. Municipios disponibles: ${municipiosDisponibles.length}`
        );
      } else {
        handleDepartamentoChange(departamentoCode); // Usar función original
        message.error("Error al seleccionar departamento");
      }
    },
    [catalogos.departamentos, catalogos.municipios, handleDepartamentoChange]
  );

  return (
    <div style={{ padding: "24px" }}>
      <Card title="Generar Factura Electrónica" style={{ marginBottom: 24 }}>
        {prefilledData && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              backgroundColor: "#e6f7ff",
              border: "1px solid #91d5ff",
              borderRadius: 6,
            }}
          >
            <div style={{ fontSize: "14px", color: "#0050b3" }}>
              <strong>Factura Prellenada con Datos del Pago y Paciente</strong>
              <br />
              <span style={{ fontSize: "12px" }}>
                <strong>Pago:</strong> Amortización #
                {prefilledData.amortizationId} • Transacción:{" "}
                {prefilledData.transactionId} • Monto: ${prefilledData.monto} •
                Proveedor: {prefilledData.provider.toUpperCase()}
              </span>
              <br />
              {authState.pacienteData && (
                <span style={{ fontSize: "12px" }}>
                  <strong>Cliente:</strong> {authState.pacienteData.nombre1}{" "}
                  {authState.pacienteData.apellido1} •{" "}
                  {authState.pacienteData.email} •{" "}
                  {authState.pacienteData.telefono}
                </span>
              )}
              <br />
              <span style={{ fontSize: "12px", color: "#1890ff" }}>
                Todos los datos están prellenados. Use "Emitir Factura
                Directamente" para envío rápido o revise/edite los campos según
                necesite
              </span>
            </div>
          </div>
        )}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            nit: "",
            receptor_tipoDocumento: "13", // DUI por defecto
            resumen_condicionOperacion: 1,
            resumen_tributos: [],
            resumen_pagos: [],
            documentoRelacionado: [],
            otrosDocumentos: [],
            apendice: [],
          }}
        >
          {/* Datos del Emisor */}
          <Card
            size="small"
            title="Datos del Emisor"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="NIT del Emisor"
                  name="nit"
                  rules={[{ required: true, message: "NIT es requerido" }]}
                >
                  <Input placeholder="NIT del emisor" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Datos del Receptor */}
          <Card
            size="small"
            title="Datos del Receptor"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="NRC (Opcional)" name="receptor_nrc">
                  <Input placeholder="Número de registro de contribuyente" />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  label="Nombre/Razón Social"
                  name="receptor_nombre"
                  rules={[{ required: true, message: "Nombre es requerido" }]}
                >
                  <Input placeholder="Nombre del receptor" />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item
                  label="Código de Actividad"
                  name="receptor_codActividad"
                  rules={[
                    {
                      required: true,
                      message: "Código de actividad es requerido",
                    },
                  ]}
                >
                  <AutoComplete
                    placeholder="Buscar código de actividad"
                    options={catalogos.actividadesEconomicas}
                    filterOption={(inputValue, option) =>
                      option?.label
                        .toLowerCase()
                        .indexOf(inputValue.toLowerCase()) !== -1
                    }
                    onSelect={(value) => {
                      // Cuando se selecciona una actividad, también se actualiza la descripción
                      const selectedActivity =
                        catalogos.actividadesEconomicas.find(
                          (act) => act.value === value
                        );
                      if (selectedActivity) {
                        // Extraer solo la descripción (parte después del código y guión)
                        const description =
                          selectedActivity.label.split(" - ")[1] ||
                          selectedActivity.label;
                        form.setFieldValue(
                          "receptor_descActividad",
                          description
                        );
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Tipo de Documento"
                  name="receptor_tipoDocumento"
                  rules={[
                    {
                      required: true,
                      message: "Tipo de documento es requerido",
                    },
                  ]}
                >
                  <Select
                    placeholder="Seleccionar tipo"
                    options={catalogos.tiposDocumentoReceptor}
                    onChange={(value) => {
                      // Cuando cambia el tipo de documento, limpiar el número
                      form.setFieldValue("receptor_numDocumento", "");
                      // Si es NIT, limpiar el NRC ya que los consumidores no lo necesitan
                      if (value === "36") {
                        // NIT
                        form.setFieldValue("receptor_nrc", "");
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Número de Documento"
                  name="receptor_numDocumento"
                  rules={[
                    {
                      required: true,
                      message: "Número de documento es requerido",
                    },
                  ]}
                >
                  <Input
                    placeholder={
                      form.getFieldValue("receptor_tipoDocumento") === "13"
                        ? "Ej: 12345678-9"
                        : form.getFieldValue("receptor_tipoDocumento") === "36"
                        ? "Ej: 12345678901234"
                        : "Número de documento"
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Descripción de Actividad"
                  name="receptor_descActividad"
                  rules={[
                    {
                      required: true,
                      message: "Descripción de actividad es requerida",
                    },
                  ]}
                >
                  <AutoComplete
                    placeholder="Buscar por descripción o se completa automáticamente"
                    options={catalogos.actividadesEconomicas.map(
                      (actividad) => ({
                        value:
                          actividad.label.split(" - ")[1] || actividad.label,
                        label:
                          actividad.label.split(" - ")[1] || actividad.label,
                        code: actividad.value,
                      })
                    )}
                    filterOption={(inputValue, option) =>
                      option?.label
                        .toLowerCase()
                        .indexOf(inputValue.toLowerCase()) !== -1
                    }
                    onSelect={(_value, option) => {
                      // Cuando se selecciona una descripción, también actualizar el código
                      if (option && "code" in option) {
                        form.setFieldValue(
                          "receptor_codActividad",
                          option.code
                        );
                      }
                    }}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Teléfono" name="receptor_telefono">
                  <Input placeholder="Número de teléfono" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Correo Electrónico" name="receptor_correo">
                  <Input placeholder="Correo electrónico" type="email" />
                </Form.Item>
              </Col>
            </Row>

            <Divider>Dirección</Divider>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  label="Departamento"
                  name="receptor_departamento"
                  rules={[
                    { required: true, message: "Departamento es requerido" },
                  ]}
                >
                  <Select
                    placeholder="Seleccionar departamento"
                    onChange={handleDepartamentoChangeImproved}
                    options={catalogos.departamentos}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Municipio"
                  name="receptor_municipio"
                  rules={[
                    { required: true, message: "Municipio es requerido" },
                  ]}
                >
                  <Select
                    placeholder="Seleccionar municipio"
                    options={getFilteredMunicipios()}
                    disabled={!getFilteredMunicipios().length}
                    showSearch
                    optionFilterProp="label"
                    notFoundContent={
                      selectedDepartamento
                        ? "No hay municipios disponibles"
                        : "Selecciona un departamento primero"
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Complemento"
                  name="receptor_complemento"
                  rules={[
                    { required: true, message: "Complemento es requerido" },
                  ]}
                >
                  <Input placeholder="Dirección completa" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Detalle de Productos */}
          <Card
            size="small"
            title="Detalle de Productos/Servicios"
            style={{ marginBottom: 16 }}
          >
            <Row justify="space-between" style={{ marginBottom: 16 }}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addItem}
                  style={{ marginRight: 8 }}
                >
                  Agregar Item Manual
                </Button>
                <Button
                  type="default"
                  icon={<ShoppingCartOutlined />}
                  onClick={() => setSidebarOpen(true)}
                >
                  Seleccionar Producto
                </Button>
              </Col>
            </Row>

            <ProductTable
              cuerpoDocumento={cuerpoDocumento}
              updateItem={updateItem}
              removeItem={removeItem}
            />
          </Card>

          {/* Resumen y Totales */}
          <Card
            size="small"
            title="Resumen y Totales"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  label="Descuento No Sujeto"
                  name="resumen_descuNoSuj"
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={formatCurrency}
                    parser={parseCurrency}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Descuento Exento" name="resumen_descuExtenta">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={formatCurrency}
                    parser={parseCurrency}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Descuento Gravado"
                  name="resumen_descuGravada"
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={formatCurrency}
                    parser={parseCurrency}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Condición de Operación"
                  name="resumen_condicionOperacion"
                >
                  <Select options={catalogos.condicionesOperacion} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={6}>
                <Form.Item label="IVA Retenido" name="resumen_ivaRete1">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={formatCurrency}
                    parser={parseCurrency}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Retención Renta" name="resumen_reteRenta">
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={formatCurrency}
                    parser={parseCurrency}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Número de pago electrónico"
                  name="resumen_numPagoElectronico"
                >
                  <Input placeholder="Número de transacción" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Información Adicional */}
          <Card
            size="small"
            title="Información Adicional"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  label="Nombre de quien entrega"
                  name="extension_nombEntrega"
                >
                  <Input placeholder="Nombre completo" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Documento de quien entrega"
                  name="extension_docuEntrega"
                >
                  <Input placeholder="DUI o documento" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Nombre de quien recibe"
                  name="extension_nombRecibe"
                >
                  <Input placeholder="Nombre completo" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Documento de quien recibe"
                  name="extension_docuRecibe"
                >
                  <Input placeholder="DUI o documento" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Observaciones" name="extension_observaciones">
                  <TextArea
                    rows={2}
                    placeholder="Observaciones adicionales sobre la factura"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Divider />

          <Row justify="end" gutter={16}>
            <Col>
              <Button type="default" icon={<SaveOutlined />} size="large">
                Guardar Borrador
              </Button>
            </Col>
            {prefilledData && (
              <Col>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => {
                    // Validar que los campos requeridos estén completos
                    form
                      .validateFields()
                      .then(() => {
                        handleSubmit(form.getFieldsValue());
                      })
                      .catch(() => {
                        message.warning(
                          "Complete todos los campos requeridos antes de enviar la factura"
                        );
                      });
                  }}
                  loading={loading}
                  size="large"
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                >
                  Emitir Factura Directamente
                </Button>
              </Col>
            )}
            <Col>
              <Button
                type="primary"
                icon={<SendOutlined />}
                htmlType="submit"
                loading={loading}
                size="large"
              >
                {prefilledData
                  ? "Vista Previa y Enviar"
                  : "Enviar Factura Electrónica"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Product Sidebar */}
      <ProductSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSelectProduct={handleSelectProduct}
      />

      {/* Floating Action Button */}
      <FloatButton
        icon={<ShoppingCartOutlined />}
        type="primary"
        style={{ right: 24, bottom: 80 }}
        onClick={() => setSidebarOpen(true)}
        tooltip="Gestionar Productos"
      />

      {/* Modal de Resumen */}
      <FacturaResumenModal
        visible={showResumenModal}
        onClose={() => setShowResumenModal(false)}
        onConfirm={handleConfirmFactura}
        loading={loading}
        formValues={resumenData}
        cuerpoDocumento={cuerpoDocumento}
        catalogos={catalogos}
      />
    </div>
  );
};

export default FacturaElectronicaForm;
